import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Popconfirm,
  DatePicker
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  FilePdfOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import { handleExport } from '../utils/exportUtils';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface InvoiceItem {
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  taxRate: number;
  total: number;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  station: {
    _id: string;
    name: string;
  };
  customer?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  invoiceType: string;
  items: InvoiceItem[];
  subtotal: number;
  taxAmount: number;
  discount: number;
  total: number;
  paymentStatus: string;
  status: string;
  invoiceDate: string;
  dueDate?: string;
}

const Invoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchCustomers();
    fetchInvoices();
  }, []);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await stationOwnerAPI.getCustomers();
      setCustomers(response);
    } catch (error) {
      // Müşteriler opsiyonel
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await stationOwnerAPI.getInvoices();
      setInvoices(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Faturalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddInvoice = () => {
    setEditingInvoice(null);
    form.resetFields();
    form.setFieldsValue({
      invoiceType: 'sale',
      invoiceDate: dayjs(),
      items: [{ description: '', quantity: 1, unit: 'adet', unitPrice: 0, taxRate: 0 }],
      discount: 0,
      paymentMethod: 'cash',
      status: 'draft'
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        stationId: values.stationId,
        customerId: values.customerId,
        invoiceType: values.invoiceType,
        items: values.items.map((item: any) => ({
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate || 0
        })),
        discount: values.discount || 0,
        paymentMethod: values.paymentMethod,
        invoiceDate: values.invoiceDate?.toISOString(),
        dueDate: values.dueDate?.toISOString(),
        notes: values.notes
      };

      if (editingInvoice) {
        await stationOwnerAPI.updateInvoice(editingInvoice._id, data);
        message.success('Fatura güncellendi');
      } else {
        await stationOwnerAPI.createInvoice(data);
        message.success('Fatura oluşturuldu');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingInvoice(null);
      fetchInvoices();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleMarkAsPaid = async (id: string) => {
    try {
      await stationOwnerAPI.markInvoiceAsPaid(id, { paidAmount: 0 });
      message.success('Fatura ödeme olarak işaretlendi');
      fetchInvoices();
    } catch (error: any) {
      message.error('İşlem başarısız');
    }
  };

  const columns = [
    {
      title: 'Fatura No',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (number: string) => <strong>{number}</strong>
    },
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station'
    },
    {
      title: 'Müşteri',
      key: 'customer',
      render: (record: Invoice) => 
        record.customer 
          ? `${record.customer.firstName} ${record.customer.lastName}`
          : '-'
    },
    {
      title: 'Tip',
      dataIndex: 'invoiceType',
      key: 'invoiceType',
      render: (type: string) => {
        const labels: any = {
          sale: 'Satış',
          purchase: 'Alış',
          service: 'Hizmet',
          other: 'Diğer'
        };
        return <Tag>{labels[type] || type}</Tag>;
      }
    },
    {
      title: 'Tutar',
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => (
        <span style={{ fontWeight: 'bold' }}>
          {total.toLocaleString('tr-TR')} ₺
        </span>
      )
    },
    {
      title: 'Ödeme Durumu',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (status: string) => {
        const colors: any = {
          paid: 'success',
          pending: 'warning',
          partial: 'processing',
          overdue: 'error',
          cancelled: 'default'
        };
        const labels: any = {
          paid: 'Ödendi',
          pending: 'Beklemede',
          partial: 'Kısmi',
          overdue: 'Gecikmiş',
          cancelled: 'İptal'
        };
        return <Tag color={colors[status]}>{labels[status] || status}</Tag>;
      }
    },
    {
      title: 'Tarih',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      render: (date: string) => dayjs(date).format('DD MMM YYYY')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 200,
      render: (_: any, record: Invoice) => (
        <Space>
          {record.paymentStatus !== 'paid' && (
            <Button
              type="link"
              icon={<CheckCircleOutlined />}
              onClick={() => handleMarkAsPaid(record._id)}
            >
              Ödendi
            </Button>
          )}
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingInvoice(record);
              form.setFieldsValue({
                ...record,
                stationId: record.station._id,
                customerId: record.customer?._id,
                invoiceDate: dayjs(record.invoiceDate),
                dueDate: record.dueDate ? dayjs(record.dueDate) : null
              });
              setModalVisible(true);
            }}
          />
          <Popconfirm
            title="Bu faturayı silmek istediğinizden emin misiniz?"
            onConfirm={async () => {
              try {
                await stationOwnerAPI.deleteInvoice(record._id);
                message.success('Fatura silindi');
                fetchInvoices();
              } catch (error: any) {
                message.error('İşlem başarısız');
              }
            }}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const totalInvoices = invoices.length;
  const totalAmount = invoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
  const paidAmount = invoices
    .filter(inv => inv.paymentStatus === 'paid')
    .reduce((sum, inv) => sum + (inv.total || 0), 0);
  const pendingAmount = invoices
    .filter(inv => inv.paymentStatus === 'pending')
    .reduce((sum, inv) => sum + (inv.total || 0), 0);

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
            <FileTextOutlined /> Fatura Yönetimi
          </Title>
        </Col>
        <Col xs={24} md={12}>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto', float: isMobile ? 'none' : 'right' }} wrap>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleAddInvoice} 
              block={isMobile}
            >
              Yeni Fatura Oluştur
            </Button>
            <Button 
              icon={<FilePdfOutlined />}
              onClick={() => handleExport(
                () => stationOwnerAPI.exportInvoicesPDF(),
                `faturalar_${Date.now()}.pdf`,
                () => message.success('PDF başarıyla indirildi')
              )}
              block={isMobile}
            >
              PDF
            </Button>
            <Button 
              icon={<FileExcelOutlined />}
              onClick={() => handleExport(
                () => stationOwnerAPI.exportInvoicesExcel(),
                `faturalar_${Date.now()}.xlsx`,
                () => message.success('Excel başarıyla indirildi')
              )}
              block={isMobile}
            >
              Excel
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Özet İstatistikler */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Fatura"
              value={totalInvoices}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Tutar"
              value={totalAmount}
              prefix={<DollarOutlined />}
              suffix="₺"
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ödenen"
              value={paidAmount}
              prefix={<CheckCircleOutlined />}
              suffix="₺"
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Bekleyen"
              value={pendingAmount}
              prefix={<DollarOutlined />}
              suffix="₺"
              precision={2}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Card styles={{ body: { padding: isMobile ? '8px' : '24px' } }}>
        <Table
          columns={columns}
          dataSource={invoices}
          rowKey="_id"
          loading={loading}
          pagination={{ 
            pageSize: 10,
            showSizeChanger: false,
            responsive: true
          }}
          scroll={{ x: 'max-content', y: isMobile ? '60vh' : undefined }}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>

      {/* Fatura Oluşturma/Düzenleme Modal */}
      <Modal
        title={editingInvoice ? 'Fatura Düzenle' : 'Yeni Fatura Oluştur'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingInvoice(null);
          form.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 900}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="stationId"
                label="İstasyon"
                rules={[{ required: true, message: 'İstasyon seçiniz' }]}
              >
                <Select placeholder="İstasyon">
                  {stations.map((station: any) => (
                    <Option key={station._id} value={station._id}>
                      {station.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="invoiceType"
                label="Fatura Tipi"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="sale">Satış</Option>
                  <Option value="purchase">Alış</Option>
                  <Option value="service">Hizmet</Option>
                  <Option value="other">Diğer</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="customerId"
            label="Müşteri (Opsiyonel)"
          >
            <Select placeholder="Müşteri seçin" allowClear>
              {customers.map((customer: any) => (
                <Option key={customer._id} value={customer._id}>
                  {customer.firstName} {customer.lastName} - {customer.phone}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => {
                  const { key, ...fieldProps } = field;
                  return (
                    <Card key={key} size="small" style={{ marginBottom: 16 }}>
                      <Row gutter={[8, 8]}>
                        <Col xs={24} sm={12} md={10}>
                          <Form.Item
                            {...fieldProps}
                            name={[field.name, 'description']}
                            label="Açıklama"
                            rules={[{ required: true }]}
                          >
                            <Input placeholder="Ürün/Hizmet adı" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                          <Form.Item
                            {...fieldProps}
                            name={[field.name, 'quantity']}
                            label="Miktar"
                            rules={[{ required: true }]}
                          >
                            <InputNumber min={0} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={3}>
                          <Form.Item
                            {...fieldProps}
                            name={[field.name, 'unit']}
                            label="Birim"
                          >
                            <Input placeholder="adet" />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                          <Form.Item
                            {...fieldProps}
                            name={[field.name, 'unitPrice']}
                            label="Birim Fiyat"
                            rules={[{ required: true }]}
                          >
                            <InputNumber min={0} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6} md={3}>
                          <Form.Item
                            {...fieldProps}
                            name={[field.name, 'taxRate']}
                            label="KDV %"
                          >
                            <InputNumber min={0} max={100} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                      </Row>
                      {fields.length > 1 && (
                        <Button
                          type="link"
                          danger
                          onClick={() => remove(field.name)}
                          style={{ marginTop: 8 }}
                        >
                          Sil
                        </Button>
                      )}
                    </Card>
                  );
                })}
                <Button type="dashed" onClick={() => add()} block>
                  + Ürün/Hizmet Ekle
                </Button>
              </>
            )}
          </Form.List>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12}>
              <Form.Item name="discount" label="İndirim (₺)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="paymentMethod" label="Ödeme Yöntemi">
                <Select>
                  <Option value="cash">Nakit</Option>
                  <Option value="card">Kart</Option>
                  <Option value="bank_transfer">Banka Transferi</Option>
                  <Option value="check">Çek</Option>
                  <Option value="other">Diğer</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item name="invoiceDate" label="Fatura Tarihi">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="dueDate" label="Vade Tarihi">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notlar">
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingInvoice ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingInvoice(null);
                form.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Invoices;

